import { useCallback, useEffect, useState } from 'react';
import { GitHubAuth } from './components/github/GitHubAuth';
import { GitHubFileTree } from './components/github/GitHubFileTree';
import { GitHubRepositorySelector } from './components/github/GitHubRepositorySelector';
import { GoogleDriveEditor } from './components/google/GoogleDriveEditor';
import { GoogleDriveExplorer } from './components/google/GoogleDriveExplorer';
import { BucketSelector } from './components/r2/BucketSelector';
import { FileEditor } from './components/r2/FileEditor';
import { FileTree } from './components/r2/FileTree';
import { AuthRequiredModal } from './components/shared/AuthRequiredModal';
import { ToastProvider } from './components/shared/Toast';
import { Auth0Provider, useAuth0 } from './auth/Auth0Context';
import { Auth0Callback } from './pages/Auth0Callback';
import { GitHubCallback } from './pages/GitHubCallback';
import { GoogleCallback } from './pages/GoogleCallback';
import { vscodeTheme } from './styles/theme';
import { clearGitHubToken, isGitHubAuthenticated } from './auth/github';
import { isGoogleAuthenticated } from './auth/google';
import type { GoogleDriveFile } from './api/google';
import { AUTH_REQUIRED_EVENT } from './api/errors';

function AppContent() {
  const { isAuthenticated, isLoading, login, user, logout } = useAuth0();
  const [activeTab, setActiveTab] = useState<'bucket' | 'github' | 'google-drive' | 'account'>(
    () => {
      const params = new URLSearchParams(window.location.search);
      const tab = params.get('tab');
      if (tab === 'github' || tab === 'account' || tab === 'google-drive') {
        return tab;
      }
      return 'bucket';
    }
  );
  const [selectedBucket, setSelectedBucket] = useState<string | null>(null);
  const [selectedR2File, setSelectedR2File] = useState<string | null>(null);
  const [selectedRepository, setSelectedRepository] = useState<{
    owner: string;
    repo: string;
    branch: string;
  } | null>(null);
  const [selectedGitHubFile, setSelectedGitHubFile] = useState<string | null>(null);
  const [isGitHubAuthed, setIsGitHubAuthed] = useState(isGitHubAuthenticated());
  const [selectedGoogleDriveFile, setSelectedGoogleDriveFile] = useState<{
    file: GoogleDriveFile;
    content: string;
    revision?: string;
  } | null>(null);
  const [googleDriveRefreshToken, setGoogleDriveRefreshToken] = useState(0);
  const [isGoogleAuthed, setIsGoogleAuthed] = useState(isGoogleAuthenticated());
  const [authRequired, setAuthRequired] = useState(false);

  useEffect(() => {
    const handleAuthChange = () => {
      setIsGitHubAuthed(isGitHubAuthenticated());
    };
    window.addEventListener('github-auth-change', handleAuthChange);
    return () => window.removeEventListener('github-auth-change', handleAuthChange);
  }, []);

  useEffect(() => {
    const handleAuthChange = () => {
      setIsGoogleAuthed(isGoogleAuthenticated());
      if (!isGoogleAuthenticated()) {
        setSelectedGoogleDriveFile(null);
      }
    };
    window.addEventListener('google-auth-change', handleAuthChange);
    return () => window.removeEventListener('google-auth-change', handleAuthChange);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setSelectedBucket(null);
      setSelectedR2File(null);
      setSelectedRepository(null);
      setSelectedGitHubFile(null);
      setSelectedGoogleDriveFile(null);
      setAuthRequired(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('tab', activeTab);
    window.history.replaceState({}, '', `${url.pathname}?${url.searchParams.toString()}${url.hash}`);
  }, [activeTab]);

  useEffect(() => {
    const handleAuthRequired = () => setAuthRequired(true);
    const handleAuthChange = () => setAuthRequired(false);
    window.addEventListener(AUTH_REQUIRED_EVENT, handleAuthRequired);
    window.addEventListener('auth0-auth-change', handleAuthChange);
    return () => {
      window.removeEventListener(AUTH_REQUIRED_EVENT, handleAuthRequired);
      window.removeEventListener('auth0-auth-change', handleAuthChange);
    };
  }, []);

  const handleGitHubLogout = useCallback(() => {
    clearGitHubToken();
    setSelectedRepository(null);
    setSelectedGitHubFile(null);
  }, []);

  const handleRepositorySelect = useCallback((repo: {
    owner: string;
    repo: string;
    branch: string;
  } | null) => {
    setSelectedRepository(repo);
    setSelectedGitHubFile(null);
  }, []);

  if (!isAuthenticated) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: vscodeTheme.colors.editorBackground,
          color: vscodeTheme.colors.textPrimary,
          fontFamily: vscodeTheme.typography.fontFamily,
        }}
      >
        <button
          type="button"
          onClick={() => {
            void login();
          }}
          disabled={isLoading}
          style={{
            padding: '12px 20px',
            backgroundColor: vscodeTheme.colors.activeBackground,
            border: `1px solid ${vscodeTheme.colors.border}`,
            color: vscodeTheme.colors.textPrimary,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            borderRadius: '6px',
            fontFamily: vscodeTheme.typography.fontFamily,
          }}
        >
          Login with Auth0
        </button>
      </div>
    );
  }

  const displayName = user?.name ?? user?.email ?? user?.nickname ?? 'User';
  const initial = displayName.charAt(0).toUpperCase();
  const isAuthBlocked = authRequired;

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        backgroundColor: vscodeTheme.colors.editorBackground,
        color: vscodeTheme.colors.textPrimary,
        fontFamily: vscodeTheme.typography.fontFamily,
        position: 'relative',
      }}
    >
      <AuthRequiredModal
        isOpen={authRequired}
        onRelogin={() => {
          void login();
        }}
      />
      <div style={{ flex: 1, backgroundColor: vscodeTheme.colors.editorBackground }}>
        {isAuthBlocked ? (
          <div
            style={{
              padding: '40px',
              color: vscodeTheme.colors.textPrimary,
              fontFamily: vscodeTheme.typography.fontFamily,
            }}
          >
            <h1 style={{ fontSize: '24px', marginBottom: '10px' }}>
              セッションが切れました
            </h1>
            <p style={{ color: vscodeTheme.colors.textSecondary }}>
              再ログインして操作を再開してください。
            </p>
          </div>
        ) : activeTab === 'bucket' && selectedBucket && selectedR2File ? (
          <FileEditor
            bucketId={selectedBucket}
            fileKey={selectedR2File}
            onFileDeleted={() => setSelectedR2File(null)}
          />
        ) : activeTab === 'github' && selectedRepository && selectedGitHubFile ? (
          <FileEditor
            githubFile={{
              owner: selectedRepository.owner,
              repo: selectedRepository.repo,
              branch: selectedRepository.branch,
              path: selectedGitHubFile,
            }}
          />
        ) : activeTab === 'google-drive' && selectedGoogleDriveFile ? (
          <GoogleDriveEditor
            file={selectedGoogleDriveFile.file}
            initialContent={selectedGoogleDriveFile.content}
            initialRevision={selectedGoogleDriveFile.revision}
            onRevisionChange={(revision) =>
              setSelectedGoogleDriveFile((prev) =>
                prev ? { ...prev, revision } : prev
              )
            }
            onFileUpdated={() => setGoogleDriveRefreshToken((prev) => prev + 1)}
          />
        ) : activeTab === 'account' ? (
          <div
            style={{
              padding: '40px',
              color: vscodeTheme.colors.textPrimary,
              fontFamily: vscodeTheme.typography.fontFamily,
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <h1 style={{ fontSize: '24px', marginBottom: '4px' }}>Account</h1>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                border: `1px solid ${vscodeTheme.colors.border}`,
                borderRadius: '8px',
                backgroundColor: vscodeTheme.colors.sidebarBackground,
                maxWidth: '420px',
              }}
            >
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  border: `1px solid ${vscodeTheme.colors.border}`,
                  backgroundColor: vscodeTheme.colors.headerBackground,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  fontWeight: 600,
                }}
              >
                {user?.picture ? (
                  <img
                    src={user.picture}
                    alt={displayName}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <span>{initial}</span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ fontSize: '16px', fontWeight: 600 }}>
                  {displayName}
                </div>
                <div style={{ fontSize: '12px', color: vscodeTheme.colors.textSecondary }}>
                  Logged in with Auth0
                </div>
              </div>
            </div>
            <div>
              <button
                type="button"
                onClick={() => {
                  void logout();
                }}
                style={{
                  padding: '8px 14px',
                  backgroundColor: vscodeTheme.colors.buttonBackground,
                  border: `1px solid ${vscodeTheme.colors.border}`,
                  color: vscodeTheme.colors.textPrimary,
                  cursor: 'pointer',
                  fontSize: '13px',
                  borderRadius: '6px',
                  fontFamily: vscodeTheme.typography.fontFamily,
                }}
              >
                Logout
              </button>
            </div>
          </div>
        ) : (
          <div
            style={{
              padding: '40px',
              color: vscodeTheme.colors.textPrimary,
              fontFamily: vscodeTheme.typography.fontFamily,
            }}
          >
            <h1 style={{ fontSize: '24px', marginBottom: '10px' }}>
              DDD Master Editor
            </h1>
            <p style={{ color: vscodeTheme.colors.textSecondary }}>
              {activeTab === 'bucket'
                ? selectedBucket
                  ? 'Select a file to start editing'
                  : 'Select a bucket to get started'
                : activeTab === 'github'
                  ? !isGitHubAuthed
                    ? 'GitHubにログインしてリポジトリを選択してください'
                    : selectedRepository
                      ? 'Select a file to view'
                      : 'Select a repository to get started'
                    : activeTab === 'google-drive'
                      ? !isGoogleAuthed
                        ? 'Google Driveに接続してください'
                        : 'Google Driveのファイルを選択してください'
                    : 'Select a workspace to get started'}
            </p>
          </div>
        )}
      </div>
      <div
        style={{
          width: '350px',
          borderLeft: `1px solid ${vscodeTheme.colors.border}`,
          overflowY: 'auto',
          backgroundColor: vscodeTheme.colors.sidebarBackground,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {isAuthBlocked ? (
            <div
              style={{
                padding: '16px',
                color: vscodeTheme.colors.textSecondary,
                fontFamily: vscodeTheme.typography.fontFamily,
              }}
            >
              ログインが必要です。再ログインしてください。
            </div>
          ) : (
            <>
              {activeTab === 'bucket' && (
                <div
                  style={{
                    padding: '8px 10px',
                    borderBottom: `1px solid ${vscodeTheme.colors.border}`,
                    backgroundColor: vscodeTheme.colors.headerBackground,
                  }}
                >
                  <BucketSelector
                    selectedBucket={selectedBucket}
                    onBucketSelect={(bucket) => {
                      setSelectedBucket(bucket);
                      setSelectedR2File(null);
                    }}
                  />
                </div>
              )}
              {activeTab === 'bucket' && selectedBucket && (
                <FileTree bucketId={selectedBucket} onFileSelect={setSelectedR2File} />
              )}
              {activeTab === 'github' && (
                <div
                  style={{
                    padding: '8px 10px',
                    borderBottom: `1px solid ${vscodeTheme.colors.border}`,
                    backgroundColor: vscodeTheme.colors.headerBackground,
                  }}
                >
                  {!isGitHubAuthed ? (
                    <GitHubAuth
                      mode="login-only"
                      returnTo={`${window.location.pathname}?tab=github${window.location.hash}`}
                    />
                  ) : (
                    <GitHubRepositorySelector
                      selectedRepository={selectedRepository}
                      onRepositorySelect={handleRepositorySelect}
                      onLogout={handleGitHubLogout}
                    />
                  )}
                </div>
              )}
              {activeTab === 'github' && isGitHubAuthed && selectedRepository && (
                <GitHubFileTree
                  repository={selectedRepository}
                  selectedFile={selectedGitHubFile}
                  onFileSelect={setSelectedGitHubFile}
                />
              )}
              {activeTab === 'google-drive' && (
                <GoogleDriveExplorer
                  selectedFileId={selectedGoogleDriveFile?.file.id ?? null}
                  onFileOpen={(file, content, revision) =>
                    setSelectedGoogleDriveFile({ file, content, revision })
                  }
                  onFileClear={() => setSelectedGoogleDriveFile(null)}
                  refreshToken={googleDriveRefreshToken}
                />
              )}
            </>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            borderTop: `1px solid ${vscodeTheme.colors.border}`,
            backgroundColor: vscodeTheme.colors.headerBackground,
          }}
        >
          <button
            type="button"
            onClick={() => setActiveTab('bucket')}
            style={{
              flex: 1,
              padding: '8px 12px',
              backgroundColor:
                activeTab === 'bucket'
                  ? vscodeTheme.colors.activeBackground
                  : 'transparent',
              border: 'none',
              color: vscodeTheme.colors.textPrimary,
              cursor: 'pointer',
              fontSize: '12px',
              fontFamily: vscodeTheme.typography.fontFamily,
              borderRight: `1px solid ${vscodeTheme.colors.border}`,
            }}
          >
            Bucket
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('github')}
            style={{
              flex: 1,
              padding: '8px 12px',
              backgroundColor:
                activeTab === 'github'
                  ? vscodeTheme.colors.activeBackground
                  : 'transparent',
              border: 'none',
              color: vscodeTheme.colors.textPrimary,
              cursor: 'pointer',
              fontSize: '12px',
              fontFamily: vscodeTheme.typography.fontFamily,
            }}
          >
            GitHub
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('google-drive')}
            style={{
              flex: 1,
              padding: '8px 12px',
              backgroundColor:
                activeTab === 'google-drive'
                  ? vscodeTheme.colors.activeBackground
                  : 'transparent',
              border: 'none',
              color: vscodeTheme.colors.textPrimary,
              cursor: 'pointer',
              fontSize: '12px',
              fontFamily: vscodeTheme.typography.fontFamily,
            }}
          >
            Google Drive
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('account')}
            style={{
              flex: 1,
              padding: '8px 12px',
              backgroundColor:
                activeTab === 'account'
                  ? vscodeTheme.colors.activeBackground
                  : 'transparent',
              border: 'none',
              color: vscodeTheme.colors.textPrimary,
              cursor: 'pointer',
              fontSize: '12px',
              fontFamily: vscodeTheme.typography.fontFamily,
              borderLeft: `1px solid ${vscodeTheme.colors.border}`,
            }}
          >
            Account
          </button>
        </div>
      </div>
    </div>
  );
}

function App() {
  const pathname = window.location.pathname;

  return (
    <Auth0Provider>
      <ToastProvider>
        {pathname === '/callback' ? (
          <GitHubCallback />
        ) : pathname === '/callback/google' ? (
          <GoogleCallback />
        ) : pathname === '/auth/callback/auth0' ? (
          <Auth0Callback />
        ) : (
          <AppContent />
        )}
      </ToastProvider>
    </Auth0Provider>
  );
}

export default App;
